import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { TraceKeyResponseTypeEnum } from '@ulysses-inc/harami_api_client'
import { FC } from 'react'
import { useHistory } from 'react-router-dom'
import DateSvg from 'src/assets/icons/date.svg?react'
import MultipleChoiceSetSvg from 'src/assets/icons/multiple_choice_set.svg?react'
import TextSvg from 'src/assets/icons/text.svg?react'
import Loading from 'src/components/loading/Loading'
import { TableBorderColor } from 'src/features/theme/KdsThemeColor'
import { useQueryTraceKeys } from 'src/features/traceKeys/api/useQueryTraceKeys'
import {
  editorPermissions,
  hasPermission,
} from 'src/libraries/roles/permissions'
import { MenuButton } from './components/MenuButton'

/**
 * 回答種別に対応するアイコンを取得
 */
const getResponseTypeIcon = (responseType: TraceKeyResponseTypeEnum) => {
  switch (responseType) {
    case TraceKeyResponseTypeEnum.Date:
      return <DateSvg width={30} height={30} />
    case TraceKeyResponseTypeEnum.Text:
      return <TextSvg width={30} height={30} />
    case TraceKeyResponseTypeEnum.ResponseSet:
      return <MultipleChoiceSetSvg width={30} height={30} />
    default:
      return null
  }
}

/**
 * 回答種別に対応するラベルを取得
 */
const getResponseTypeLabel = (responseType: TraceKeyResponseTypeEnum) => {
  switch (responseType) {
    case TraceKeyResponseTypeEnum.Date:
      return '日時'
    case TraceKeyResponseTypeEnum.Text:
      return 'テキスト'
    case TraceKeyResponseTypeEnum.ResponseSet:
      return 'セット選択肢'
    default:
      return String(responseType)
  }
}

/**
 * 検索キー一覧テーブル
 *
 * - 検索キー名と回答種別を表示
 * - ソートはAPI側で実施
 * - ページネーションなし
 */
export const TraceKeysTable: FC = () => {
  const history = useHistory()
  const { data: traceKeys = [], isLoading } = useQueryTraceKeys()
  const isEditor = hasPermission(editorPermissions)

  if (isLoading) {
    return <Loading />
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
      <Table>
        <TableHead sx={{ backgroundColor: 'white' }}>
          <TableRow
            sx={{
              borderBottom: `1px solid ${TableBorderColor}`,
            }}
          >
            <TableCell
              size={'small'}
              sx={{
                whiteSpace: 'normal',
                backgroundColor: 'white',
              }}
            >
              検索キー名
            </TableCell>
            <TableCell
              size="small"
              sx={{
                fontWeight: 'bold',
                whiteSpace: 'normal',
                backgroundColor: 'white',
              }}
            >
              回答種別
            </TableCell>
            <TableCell
              size="small"
              sx={{ minWidth: 96, backgroundColor: 'white' }}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {traceKeys.map(traceKey => {
            const icon = getResponseTypeIcon(traceKey.responseType)
            const label = getResponseTypeLabel(traceKey.responseType)
            return (
              <TableRow
                key={traceKey.id}
                hover={isEditor}
                sx={{ cursor: isEditor ? 'pointer' : 'default' }}
                onClick={
                  isEditor
                    ? () => history.push(`/traceKeys/${traceKey.id}/edit`)
                    : undefined
                }
              >
                <TableCell
                  size="small"
                  sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  {traceKey.name}
                </TableCell>
                <TableCell size="small" sx={{ whiteSpace: 'normal' }}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    {icon && (
                      <Stack
                        sx={{ width: 28, height: 28 }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        {icon}
                      </Stack>
                    )}
                    <span>{label}</span>
                  </Stack>
                </TableCell>
                <TableCell size="small" sx={{ minWidth: 96 }} align="right">
                  {isEditor && <MenuButton traceKey={traceKey} />}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
